import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, Brain, Moon, Shield, AlertTriangle } from 'lucide-react';

const scales = [
  {
    title: 'PHQ-9 (Depresyon Değerlendirme Ölçeği)',
    purpose: 'Depresyon belirtilerinin şiddetini ölçmek için kullanılır.',
    basis: 'PHQ-9, DSM kriterlerine dayalı olarak geliştirilmiş ve dünya genelinde klinik çalışmalarda yaygın olarak kullanılan bir ölçektir.',
    usage: 'Birinci basamak sağlık hizmetleri ve psikolojik değerlendirme süreçleri.',
    ref: 'Kroenke K, Spitzer RL, Williams JB. The PHQ-9: Validity of a brief depression severity measure.',
    icon: Shield,
    color: '#D88A61',
  },
  {
    title: 'GAD-7 (Anksiyete Ölçeği)',
    purpose: 'Genel anksiyete düzeyini değerlendirmek için kullanılır.',
    basis: 'GAD-7, yaygın anksiyete bozukluğu taraması için geliştirilmiş ve geçerliliği yüksek bir ölçektir.',
    usage: 'Klinik değerlendirme ve psikolojik tarama.',
    ref: 'Spitzer RL, Kroenke K, Williams JB, Löwe B.',
    icon: Brain,
    color: '#6F90B1',
  },
  {
    title: 'ISI (Uyku / Uykusuzluk Değerlendirmesi)',
    purpose: 'Uyku kalitesi ve uykusuzluk düzeyini ölçer.',
    basis: 'Uyku araştırmalarında yaygın olarak kullanılan ve geçerliliği kanıtlanmış bir ölçektir.',
    usage: 'Uyku bozuklukları ve uyku kalitesi değerlendirmesi.',
    ref: null,
    icon: Moon,
    color: '#D9A258',
  },
  {
    title: 'MoCA (Bilişsel Değerlendirme)',
    purpose: 'Hafıza, dikkat ve bilişsel fonksiyonları değerlendirir.',
    basis: 'Hafif bilişsel bozuklukların erken tespiti için geliştirilmiş yaygın bir testtir.',
    usage: 'Nörolojik ve bilişsel değerlendirme süreçleri.',
    ref: null,
    icon: Brain,
    color: '#5A9E7A',
  },
];

const sources = [
  'American Psychological Association (APA)',
  'National Institutes of Health (NIH)',
  'World Health Organization (WHO)',
  'Sleep Research Society',
];

export default function BilimselDayanakPage() {
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #15263A 0%, #1E3348 80px, #F8F6F3 80px, #F8F6F3 100%)' }}>
      <Helmet>
        <title>Bilimsel Dayanak — DoktorCebinde</title>
        <meta name="description" content="DoktorCebinde platformunun bilimsel temelleri, kullanılan değerlendirme yaklaşımları ve referanslar." />
      </Helmet>
      <Header />

      <article style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 80px' }}>

        {/* Title */}
        <h1 style={{ fontSize: 32, fontWeight: 700, color: '#1A2B3C', textAlign: 'center', marginBottom: 24 }} data-testid="bilimsel-title">
          Bilimsel Dayanak
        </h1>

        {/* Intro */}
        <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '28px 32px', marginBottom: 40 }} data-testid="bilimsel-intro">
          <p style={{ fontSize: 15, lineHeight: 1.7, color: '#435160', margin: 0 }}>
            DoktorCebinde platformu, nörobilim, klinik psikoloji ve davranış bilimleri alanlarında yapılmış bilimsel çalışmalar temel alınarak geliştirilmiştir.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.7, color: '#435160', margin: '14px 0 0' }}>
            Bu platform, kullanıcıların zihinsel sağlık ve bilişsel durumlarını daha iyi anlamalarına yardımcı olmak amacıyla, literatürde yer alan değerlendirme yaklaşımlarından esinlenen bir sistem kullanır.
          </p>
        </div>

        {/* Section: Scales */}
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A2B3C', marginBottom: 10 }}>
          Kullanılan Değerlendirme Yaklaşımları
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: '#687482', marginBottom: 28 }}>
          Platformda kullanılan değerlendirme mantıkları, klinik ve akademik araştırmalarda yaygın olarak kullanılan aşağıdaki bilimsel ölçeklerin temel prensiplerinden esinlenmektedir.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 48 }} data-testid="bilimsel-scales">
          {scales.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, overflow: 'hidden' }} data-testid={`scale-card-${i}`}>
                <div style={{ height: 3, background: s.color }} />
                <div style={{ padding: '24px 28px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: `${s.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={18} style={{ color: s.color }} />
                    </div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1A2B3C', margin: 0 }}>{s.title}</h3>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Ne işe yarar</span>
                      <p style={{ fontSize: 14, lineHeight: 1.6, color: '#435160', margin: '4px 0 0' }}>{s.purpose}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Bilimsel dayanak</span>
                      <p style={{ fontSize: 14, lineHeight: 1.6, color: '#435160', margin: '4px 0 0' }}>{s.basis}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Kullanım alanı</span>
                      <p style={{ fontSize: 14, lineHeight: 1.6, color: '#435160', margin: '4px 0 0' }}>{s.usage}</p>
                    </div>
                    {s.ref && (
                      <div style={{ marginTop: 4, padding: '10px 14px', background: '#FAF8F5', borderRadius: 8, border: '1px solid #f0ece6' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8' }}>Referans: </span>
                        <span style={{ fontSize: 12, color: '#687482', fontStyle: 'italic' }}>{s.ref}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Section: Platform Approach */}
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A2B3C', marginBottom: 16 }}>
          Platformun Yaklaşımı
        </h2>
        <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '28px 32px', marginBottom: 48 }} data-testid="bilimsel-approach">
          <p style={{ fontSize: 15, lineHeight: 1.7, color: '#435160', margin: '0 0 16px' }}>
            DoktorCebinde, bu testlerin birebir dijital kopyasını sunmaz.
          </p>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A2B3C', margin: '0 0 8px' }}>Bu platform:</p>
            <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li style={{ fontSize: 14, lineHeight: 1.6, color: '#435160' }}>bilimsel testlerin temel prensiplerinden yararlanır</li>
              <li style={{ fontSize: 14, lineHeight: 1.6, color: '#435160' }}>kullanıcı dostu bir değerlendirme sistemi sunar</li>
              <li style={{ fontSize: 14, lineHeight: 1.6, color: '#435160' }}>günlük hayata uygulanabilir yorumlar üretir</li>
            </ul>
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A2B3C', margin: '0 0 8px' }}>Amaç:</p>
            <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li style={{ fontSize: 14, lineHeight: 1.6, color: '#435160' }}>tanı koymak değil</li>
              <li style={{ fontSize: 14, lineHeight: 1.6, color: '#435160' }}>farkındalık oluşturmak</li>
              <li style={{ fontSize: 14, lineHeight: 1.6, color: '#435160' }}>erken değişimleri fark etmeye yardımcı olmak</li>
            </ul>
          </div>
        </div>

        {/* Section: Sources */}
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A2B3C', marginBottom: 16 }}>
          Bilimsel Kaynaklar
        </h2>
        <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '24px 28px', marginBottom: 48 }} data-testid="bilimsel-sources">
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#435160', margin: '0 0 14px' }}>
            Bu platform geliştirilirken aşağıdaki kurumların yayınları ve bilimsel prensipleri dikkate alınmıştır:
          </p>
          <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sources.map((src, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#1A2B3C', fontWeight: 500 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6F90B1', flexShrink: 0 }} />
                {src}
              </li>
            ))}
          </ul>
        </div>

        {/* Section: Disclaimer */}
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1A2B3C', marginBottom: 16 }}>
          Sorumluluk Reddi
        </h2>
        <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '24px 28px' }} data-testid="bilimsel-disclaimer">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <AlertTriangle size={18} style={{ color: '#D88A61' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#D88A61' }}>Önemli Bilgilendirme</span>
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: '#435160', margin: '0 0 12px' }}>
            DoktorCebinde platformu tıbbi tanı koyma amacı taşımaz.
          </p>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#435160', margin: '0 0 4px' }}>Sunulan değerlendirmeler:</p>
          <ul style={{ margin: '0 0 14px', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <li style={{ fontSize: 14, lineHeight: 1.6, color: '#435160' }}>bilgilendirme amaçlıdır</li>
            <li style={{ fontSize: 14, lineHeight: 1.6, color: '#435160' }}>klinik değerlendirme yerine geçmez</li>
          </ul>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: '#435160', margin: 0, fontWeight: 500 }}>
            Herhangi bir sağlık sorunu durumunda bir sağlık uzmanına başvurulmalıdır.
          </p>
        </div>

      </article>

      <Footer />
    </div>
  );
}
